import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { Modal } from "./ui";

/** Sent on window after a note goes off, so an open Feedback page lists it. */
export const FEEDBACK_SENT = "feedback:sent";

export const FEEDBACK_KINDS = [
  { key: "PROBLEM", label: "Something's wrong", hint: "A wrong figure, an error, something that doesn't work" },
  { key: "IDEA", label: "An idea", hint: "Something you'd like it to do, or do differently" },
  { key: "RATES", label: "A card's rates are off", hint: "A rate, cap or milestone that isn't what your bank says" },
] as const;

/**
 * "Suggest a change", from anywhere in the app. It sends the page you are on
 * and the version you are running with the note, so the owner sees what you
 * saw.
 */
export function FeedbackButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<(typeof FEEDBACK_KINDS)[number]["key"]>("IDEA");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  function close() {
    setOpen(false);
    setSent(false);
    setError(null);
  }

  async function send(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/feedback", {
        kind,
        message,
        page: location.pathname + location.search,
        version: `${__APP_VERSION__} · ${__APP_COMMIT__}`,
      });
      setSent(true);
      window.dispatchEvent(new Event(FEEDBACK_SENT));
      setMessage("");
      setKind("IDEA");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not send that — try again");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button onClick={() => setOpen(true)} className="mt-2 block text-xs font-medium text-accent-700 underline hover:text-accent-800">
        Suggest a change
      </button>
      <Modal open={open} onClose={close} title="Suggest a change">
        {sent ? (
          <div className="space-y-3 text-sm text-ink-700">
            <p>Thank you — that's with the owner now.</p>
            <p>
              You can see what you've sent, and any reply, under{" "}
              <Link to="/feedback" className="font-medium text-accent-700 underline" onClick={close}>
                Feedback
              </Link>
              .
            </p>
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => setSent(false)}>Send another</button>
              <button className="btn-primary" onClick={close}>Done</button>
            </div>
          </div>
        ) : (
          <form onSubmit={send} className="space-y-4">
            <fieldset>
              <legend className="label mb-1.5">What is it?</legend>
              <div className="space-y-1.5">
                {FEEDBACK_KINDS.map((k) => (
                  <label key={k.key} className="flex items-start gap-2 text-sm text-ink-800">
                    <input
                      type="radio"
                      name="fb-kind"
                      className="mt-1 h-4 w-4 border-ink-300 text-accent-600 focus:ring-accent-500"
                      checked={kind === k.key}
                      onChange={() => setKind(k.key)}
                    />
                    <span>
                      {k.label}
                      <span className="block text-xs text-ink-500">{k.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div>
              <label className="label" htmlFor="fb-message">Tell us</label>
              <textarea
                id="fb-message"
                className="input min-h-[7rem]"
                required
                minLength={3}
                maxLength={4000}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  kind === "RATES"
                    ? "Which card, which rule, and what your bank's terms say — a link helps."
                    : kind === "PROBLEM"
                      ? "What you did, what you expected, and what happened instead."
                      : "What would make this more useful to you?"
                }
              />
              <p className="mt-1 text-xs text-ink-500">
                The page you're on and the app's version are sent with it. Don't include card numbers.
              </p>
            </div>
            {error && <p className="text-sm text-red-700">{error}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-secondary" onClick={close}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={busy || message.trim().length < 3}>
                {busy ? "Sending…" : "Send"}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}
